import { useState } from 'react'
import { CampoDeCaixa, CampoDeSelecao, CampoDeTexto } from '../../components/campos'
import { TEXTOS } from '../../lib/glossario'
import type { FiltrosDeCommits as Filtros, SetorDoAutor } from '../../types/repositorios'
import { ORDEM_DOS_SETORES, rotuloDoSetor } from './setores'

interface Props {
  filtros: Filtros
  mesFechado: boolean
  aoMudar: (filtros: Filtros) => void
}

/**
 * Os filtros da lista de commits.
 *
 * A busca só é aplicada ao sair do campo ou ao apertar Enter: buscar a cada tecla dispararia uma
 * consulta por letra, e a lista pulando enquanto a pessoa digita é pior do que esperar o Enter.
 * "Incluir o que chegou hoje" só existe no mês corrente — no fechado não há hoje dentro dele.
 * O filtro da equipe interna tem dois valores, e não três: o padrão do relatório já exclui quem
 * está no Diário de Bordo, e "todos juntos" misturaria o que o dono mandou separar.
 *
 * @param props os filtros, se o mês está fechado e o que fazer ao mudar
 * @returns a barra de filtros
 */
export function FiltrosDeCommits({ filtros, mesFechado, aoMudar }: Props) {
  const t = TEXTOS.repositorios.commits.filtros
  const te = TEXTOS.repositorios.equipeInterna
  const [busca, setBusca] = useState(filtros.busca ?? '')

  const solicitacao = filtros.semSolicitacao === undefined ? '' : filtros.semSolicitacao ? 'sem' : 'com'
  const tipo = filtros.integracao === undefined ? '' : filtros.integracao ? 'integracao' : 'trabalho'

  function mudar(parcial: Partial<Filtros>) {
    aoMudar({ ...filtros, ...parcial, pagina: 0 })
  }

  return (
    <div className="cartao grid gap-3 p-4 md:grid-cols-5">
      <CampoDeSelecao
        rotulo={t.setor}
        value={filtros.setor ?? ''}
        onChange={(evento) => mudar({ setor: (evento.target.value || undefined) as SetorDoAutor | undefined })}
      >
        <option value="">{t.todosOsSetores}</option>
        {ORDEM_DOS_SETORES.map((setor) => (
          <option key={setor} value={setor}>
            {rotuloDoSetor(setor)}
          </option>
        ))}
      </CampoDeSelecao>
      <CampoDeSelecao
        rotulo={t.solicitacao}
        value={solicitacao}
        onChange={(evento) =>
          mudar({ semSolicitacao: evento.target.value === '' ? undefined : evento.target.value === 'sem' })
        }
      >
        <option value="">{t.qualquer}</option>
        <option value="sem">{t.soSem}</option>
        <option value="com">{t.soCom}</option>
      </CampoDeSelecao>
      <CampoDeSelecao
        rotulo={t.tipo}
        value={tipo}
        onChange={(evento) =>
          mudar({ integracao: evento.target.value === '' ? undefined : evento.target.value === 'integracao' })
        }
      >
        <option value="">{t.qualquer}</option>
        <option value="trabalho">{t.soTrabalho}</option>
        <option value="integracao">{t.soIntegracao}</option>
      </CampoDeSelecao>
      <CampoDeSelecao
        rotulo={te.filtro}
        value={filtros.equipeInterna ? 'equipe' : ''}
        onChange={(evento) => mudar({ equipeInterna: evento.target.value === 'equipe' || undefined })}
      >
        <option value="">{te.foraDoRelatorio}</option>
        <option value="equipe">{te.soAEquipe}</option>
      </CampoDeSelecao>
      <CampoDeTexto
        rotulo={t.busca}
        ajuda={t.buscaAjuda}
        value={busca}
        onChange={(evento) => setBusca(evento.target.value)}
        onBlur={() => busca !== (filtros.busca ?? '') && mudar({ busca: busca || undefined })}
        onKeyDown={(evento) => {
          if (evento.key === 'Enter') {
            mudar({ busca: busca || undefined })
          }
        }}
      />
      {!mesFechado && (
        <div className="md:col-span-5">
          <CampoDeCaixa
            rotulo={t.incluirHoje}
            checked={filtros.incluirHoje ?? false}
            onChange={(evento) => mudar({ incluirHoje: evento.target.checked || undefined })}
          />
        </div>
      )}
    </div>
  )
}
